import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  defaultConfigMobile,
  defaultConfigMobileColors,
  defaultConfigMobileColorsValidation,
  defaultConfigMobileConfiguration,
  defaultConfigMobileFirebaseValidation,
  defaultConfigMobileLayout,
} from '../constants';
import { configMobileDataCast, getConfigMobileParams } from '../helpers';
import {
  IConfigMobile,
  IConfigMobileColors,
  IConfigMobileColorsValidation,
  IConfigMobileConfiguration,
  IConfigMobileFirebaseValidation,
  IConfigMobileLayout,
  IResponseConfigMobile,
  TConfigMobileColorField,
  TConfigMobileFirebaseField,
  TConfigMobileFirebaseValidationKey,
} from '../types';
import client from 'src/services/client';
import { IValidation, LoadingStatus } from 'src/types/main';
import { emptyStringValidation, validationColors } from 'src/utils/validation';
import logger from '~/services/logger';
import { scrollToErrorElement } from '~/utils/helpers';

class ConfigMobileStore {
  constructor() {
    makeObservable(this);
  }

  @observable colors: IConfigMobileColors = defaultConfigMobileColors;
  @observable layout: IConfigMobileLayout = defaultConfigMobileLayout;
  @observable configuration: IConfigMobileConfiguration = defaultConfigMobileConfiguration;

  @observable loading: LoadingStatus = LoadingStatus.Init;
  @observable isEdit: boolean = false;

  @observable focusField: TConfigMobileColorField | null = null;
  @action setFocusField = (field: TConfigMobileColorField | null) => {
    this.focusField = field;
  };

  @computed get isFocusFieldValue(): string {
    if (this.focusField) return this.colors[this.focusField];
    return '';
  }

  @action changeColors = (value: Partial<IConfigMobileColors>) => {
    this.colors = { ...this.colors, ...value };
    this.isEdit = true;

    // update validation
    if (!this.colorsValidation.isValid) this.setColorsValidation();
  };

  @action changeLayout = (value: Partial<IConfigMobileLayout>) => {
    this.layout = { ...this.layout, ...value };
    this.isEdit = true;
  };

  @action changeConfigTimePicker = (value: IConfigMobileLayout['timePickerPosition']) => {
    this.layout.timePickerPosition = value;
    this.isEdit = true;
  };

  @action changeConfigFirebase = (value: Partial<IConfigMobileConfiguration['firebase']>) => {
    this.configuration.firebase = { ...this.configuration.firebase, ...value };
    this.isEdit = true;

    // update validation
    if (!this.firebaseValidation.isValid) this.setFirebaseValidation();
  };

  @action changeConfigFirebaseWeb = (value: Partial<IConfigMobileConfiguration['firebaseWeb']>) => {
    this.configuration.firebaseWeb = { ...this.configuration.firebaseWeb, ...value };
    this.isEdit = true;
  };

  private configMobile: IConfigMobile = defaultConfigMobile;
  @action public setData = (data: IResponseConfigMobile) => {
    this.configMobile = configMobileDataCast(data);

    this.colors = this.configMobile.colors;
    this.layout = this.configMobile.layout;
    this.configuration = this.configMobile.configuration;
  };

  @action submit = () => {
    this.setValidations();
    if (!this.isValid) {
      scrollToErrorElement();
      return;
    }

    this.updateSettings();
  };

  private get params() {
    return getConfigMobileParams({ colors: this.colors, layout: this.layout, configuration: this.configuration });
  }

  @action updateSettings = async () => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.put('/v3/settings/consumer-channels/mobile', this.params);

      this.configMobile = { colors: this.colors, layout: this.layout, configuration: this.configuration };
      this.isEdit = false;
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'ConfigMobileStore updateSettings', showModal: true });
    }
  };

  @computed get isValid(): boolean {
    return this.colorsValidation.isValid && this.firebaseValidation.isValid;
  }

  @action setValidations = () => {
    this.setColorsValidation();
    this.setFirebaseValidation();
  };

  @observable colorsValidation: IConfigMobileColorsValidation = cloneDeep(defaultConfigMobileColorsValidation);
  private setColorsValidation = () => {
    this.colorsValidation = cloneDeep(defaultConfigMobileColorsValidation);

    const colorsValidationKeys = Object.keys(this.colorsValidation);
    colorsValidationKeys.shift(); // delete isValid key

    colorsValidationKeys.forEach((key) => {
      let keyValue = this.colorsValidation[key as keyof IConfigMobileColorsValidation] as IValidation;

      keyValue = validationColors(this.colors[key as TConfigMobileColorField], {
        rgb: false,
        html: false,
        hsla: false,
      });

      // reset error text
      keyValue.errorText = undefined;

      (this.colorsValidation[key as keyof IConfigMobileColorsValidation] as IValidation) = keyValue;
      if (!keyValue.isValid) this.colorsValidation.isValid = false;
    });
  };

  @observable firebaseValidation: IConfigMobileFirebaseValidation = cloneDeep(defaultConfigMobileFirebaseValidation);
  private setFirebaseValidation = () => {
    this.firebaseValidation = cloneDeep(defaultConfigMobileFirebaseValidation);

    const firebaseValidationKeys = Object.keys(this.firebaseValidation);
    firebaseValidationKeys.shift(); // delete isValid key

    firebaseValidationKeys.forEach((key) => {
      const keyValue = this.firebaseValidation[key as TConfigMobileFirebaseValidationKey] as IValidation;

      [keyValue.isValid, this.firebaseValidation.isValid, keyValue.errorText] = emptyStringValidation(
        this.configuration.firebase[key as TConfigMobileFirebaseField],
        keyValue.isValid,
        this.firebaseValidation.isValid
      );

      // reset error text
      keyValue.errorText = undefined;

      (this.firebaseValidation[key as TConfigMobileFirebaseValidationKey] as IValidation) = keyValue;
    });
  };

  @action clearStore = () => {
    this.setFocusField(null);
    this.isEdit = false;

    this.colors = this.configMobile.colors;
    this.layout = this.configMobile.layout;
    this.configuration = this.configMobile.configuration;

    this.colorsValidation = cloneDeep(defaultConfigMobileColorsValidation);
    this.firebaseValidation = cloneDeep(defaultConfigMobileFirebaseValidation);
  };
}

const configMobileStore = new ConfigMobileStore();

export default configMobileStore;
